import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { api, setToken, getToken } from '@/lib/api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip if OAuth callback in progress
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }
    if (!getToken() && !document.cookie.includes('session_token')) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data.user);
      setTenant(data.tenant);
    } catch (e) {
      setToken(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { checkAuth(); }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    setToken(data.token);
    setUser(data.user);
    const me = await api.get('/auth/me');
    setTenant(me.data.tenant);
    return data;
  };

  const registerCompany = async (payload) => {
    const { data } = await api.post('/auth/register-company', payload);
    setToken(data.token);
    setUser(data.user);
    setTenant(data.tenant);
    return data;
  };

  const loginWithSessionId = async (sessionId) => {
    const { data } = await api.post('/auth/emergent/session', {}, { headers: { 'X-Session-ID': sessionId } });
    setToken(data.token);
    setUser(data.user);
    const me = await api.get('/auth/me');
    setTenant(me.data.tenant);
    return data;
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch {}
    setToken(null);
    setUser(null);
    setTenant(null);
  };

  return (
    <AuthContext.Provider value={{ user, tenant, loading, login, registerCompany, loginWithSessionId, logout, refresh: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
